<template>
    <div class="mx-2.5 mt-2 mb-12">
        <div
            v-if="selectedBenefit?.is_active !== false"
            class="grid grid-cols-1 max-sm:grid-rows-3 sm:grid-cols-3 border border-slate-200 dark:border-slate-700 rounded-lg sm:py-4 max-sm:px-4 sm:divide-x max-sm:divide-y divide-slate-200 mb-1"
        >
            <div
                v-if="!isCountBased || isNcred"
                class="text-center sm:px-6 max-sm:py-4 flex sm:flex-col items-center sm:justify-center justify-between"
            >
                <div class="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    Value up to
                </div>
                <template v-if="isNcred">
                    <div class="font-medium text-slate-800 dark:text-slate-100">
                        {{ displayAmount }} {{ filters.pluralizeUnit(displayAmount, selectedBenefit?.benefit?.values_summary?.unit_label || 'night credits') }}/{{ periodTypeNoun }}
                    </div>
                </template>
                <template v-else>
                    <Tooltip
                        v-if="isOverrideMonth"
                        :text="`Normal value: ${filters.formatSmartMoney(normalMonthlyValue)}/month`"
                        position="bottom"
                    >
                        <template #trigger>
                            <div
                                class="font-medium text-slate-800 dark:text-slate-100 cursor-help"
                            >
                                {{ filters.formatSmartMoney(displayAmount) }}/{{periodTypeNoun}}
                            </div>
                        </template>
                    </Tooltip>
                    <div v-else class="font-medium text-slate-800 dark:text-slate-100">
                        {{ filters.formatSmartMoney(displayAmount) }}/{{periodTypeNoun}}
                    </div>
                </template>
            </div>
            <div
                class="text-center sm:px-6 max-sm:py-4 flex sm:flex-col items-center sm:justify-center justify-between"
                v-if="isCountBased && !isNcred"
            >
                <div class="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    Use up to
                </div>
                <div class="font-medium text-slate-800 dark:text-slate-100">
                    {{ selectedBenefit?.benefit?.value_details?.count }}
                    {{
                        selectedBenefit?.benefit?.value_details.count > 1
                            ? 'times'
                            : 'time'
                    }}
                </div>
            </div>
            <div
                class="text-center sm:px-6 max-sm:py-4 flex sm:flex-col items-center sm:justify-center justify-between"
            >
                <div class="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {{ currentPeriodLabel }}
                </div>
                <div class="font-medium text-slate-800 dark:text-slate-100">
                    {{
                        isCountBased
                            ? currentPeriodUsage
                            : filters.formatSmartMoney(currentPeriodUsage)
                    }}
                </div>
            </div>
            <div
                class="text-center sm:px-6 max-sm:py-4 flex sm:flex-col items-center sm:justify-center justify-between"
            >
                <div class="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    Utilization
                </div>
                <div class="flex items-center justify-center gap-2">
                    <div
                        class="font-medium text-slate-800 dark:text-slate-100 order-2 sm:order-1"
                    >
                        {{ utilizationPercentage }}%
                    </div>
                    <!-- Utilization Graph -->
                    <div class="relative w-8 h-5 order-1 sm:order-2">
                        <svg class="w-8 h-8" viewBox="0 0 32 32">
                            <path
                                d="M 4 16 A 12 12 0 0 1 28 16"
                                fill="none"
                                stroke="#FED1C7"
                                stroke-width="4"
                            />
                            <path
                                d="M 4 16 A 12 12 0 0 1 28 16"
                                fill="none"
                                stroke="#FD421D"
                                stroke-width="4"
                                :stroke-dasharray="`${(utilizationPercentage / 100) * 37.7} 37.7`"
                                class="transition-all duration-300"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
        <!-- First Year Anniversary Warning -->
        <div
            v-if="selectedBenefit?.benefit?.show_first_year_notice"
            class="flex items-start gap-1"
        >
            <Icon
                icon="warning"
                class="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
            />
            <p class="text-sm text-red-600">
                This benefit is only available after your card's first
                anniversary.
            </p>
        </div>
        <!-- Inactive Benefit Warning -->
        <div
            v-if="selectedBenefit?.is_active === false"
            class="flex items-start gap-1 mt-2"
        >
            <Icon
                icon="warning"
                class="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
            />
            <p class="text-sm text-amber-600">
                This benefit is no longer active on this card. You can
                still view your historical usage.
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import Tooltip from '@/components/tooltip.vue';
import filters from '@/filters';

const props = defineProps({
    selectedBenefit: { type: Object, default: null },
    isCountBased: { type: Boolean, required: true },
    isOverrideMonth: { type: Boolean, required: true },
    normalMonthlyValue: { type: Number, default: 0 },
    displayAmount: { type: Number, default: 0 },
    currentPeriodUsage: { type: Number, default: 0 },
    utilizationPercentage: { type: Number, default: 0 },
    periodTypeNoun: { type: String, default: '' },
    currentPeriodLabel: { type: String, default: 'Used this period' },
});

const isNcred = computed(
    () => props.selectedBenefit?.benefit?.values_summary?.currency === 'NCRED'
);
</script>
